import { SessionGoalTimingType } from '../../../../../../../utils/types';

import type { Moment } from 'moment';
import type { TimeView } from '@mui/x-date-pickers/models';
import type { FTimePickerChangeContext } from '../../../../../../../components/baseV2';
import type { ISessionGoalTiming } from '../../../../../../../utils/types';

export function hasGoalTimingOverlap(
  draftTiming: ISessionGoalTiming | null,
  timings: ISessionGoalTiming[],
  editingIndex: number | null,
) {
  const startOffset = draftTiming?.start_offset_seconds;
  const endOffset = draftTiming?.end_offset_seconds;
  if (startOffset == null || endOffset == null || endOffset <= startOffset) return false;

  return timings.some((timing, index) => {
    if (index === editingIndex) return false;
    if (timing.start_offset_seconds == null || timing.end_offset_seconds == null) return false;
    return startOffset < timing.end_offset_seconds && endOffset > timing.start_offset_seconds;
  });
}

export function isGoalTimeDisabled(
  value: Moment,
  view: TimeView | 'meridiem',
  sessionStart: Moment | undefined,
  expectedDuration: number | undefined,
) {
  if (!value.isValid() || !sessionStart?.isValid() || expectedDuration == null) return true;

  const sessionEnd = sessionStart.clone().add(expectedDuration, 'seconds');
  const rangeStart = sessionStart
    .clone()
    .hour(value.hour())
    .minute(value.minute())
    .second(0)
    .millisecond(0);
  const rangeEnd = rangeStart.clone();

  if (view === 'hours') {
    rangeStart.startOf('hour');
    rangeEnd.endOf('hour');
  } else if (view === 'meridiem') {
    rangeStart.hour(value.hour() < 12 ? 0 : 12).startOf('hour');
    rangeEnd.hour(value.hour() < 12 ? 11 : 23).endOf('hour');
  } else if (view === 'seconds') {
    rangeStart.second(value.second());
    rangeEnd.second(value.second());
  }

  if (!rangeEnd.isBefore(sessionStart) && !rangeStart.isAfter(sessionEnd)) return false;

  rangeStart.add(1, 'day');
  rangeEnd.add(1, 'day');
  return rangeEnd.isBefore(sessionStart) || rangeStart.isAfter(sessionEnd);
}

export function getDisabledGoalMeridiem(
  sessionStart: Moment | undefined,
  expectedDuration: number | undefined,
): Array<'am' | 'pm'> {
  if (!sessionStart) return ['am', 'pm'];

  const disabledMeridiem: Array<'am' | 'pm'> = [];
  if (isGoalTimeDisabled(sessionStart.clone().hour(0), 'meridiem', sessionStart, expectedDuration)) {
    disabledMeridiem.push('am');
  }
  if (isGoalTimeDisabled(sessionStart.clone().hour(12), 'meridiem', sessionStart, expectedDuration)) {
    disabledMeridiem.push('pm');
  }
  return disabledMeridiem;
}

export function resolveGoalTimeChange(
  value: Moment | null,
  sessionStart: Moment | undefined,
  expectedDuration: number | undefined,
  context?: FTimePickerChangeContext,
) {
  if (context?.view === undefined && context?.validationError != null) return null;
  const resolvedTime = resolveSelectedTime(value, sessionStart, expectedDuration);
  if (resolvedTime || !value || !sessionStart || expectedDuration == null) return resolvedTime;
  if (context?.view !== 'hours' && context?.view !== 'meridiem') return null;
  if (isGoalTimeDisabled(value, 'meridiem', sessionStart, expectedDuration)) return null;

  if (isGoalTimeDisabled(value, 'hours', sessionStart, expectedDuration)) {
    if (context.view !== 'meridiem') return null;
    if ((value.hour() < 12) === (sessionStart.hour() < 12)) return sessionStart.clone();
    return sessionStart.clone().add(expectedDuration, 'seconds');
  }
  if (context.view !== 'hours') return null;

  // Changing hours can carry a minute that is invalid in the new boundary hour.
  if (value.hour() === sessionStart.hour() && value.minute() < sessionStart.minute()) {
    return sessionStart.clone();
  }
  return sessionStart.clone().add(expectedDuration, 'seconds');
}

export const buildSessionTime = (sessionStart: Moment, offsetSeconds: number | null | undefined) => {
  if (offsetSeconds == null) return null;
  return sessionStart.clone().add(offsetSeconds, 'seconds');
};

export const resolveSelectedTime = (
  selectedTime: Moment | null,
  sessionStart: Moment | undefined,
  expectedDuration: number | undefined,
) => {
  if (!selectedTime?.isValid() || !sessionStart?.isValid() || expectedDuration == null) return null;

  const sessionEnd = sessionStart.clone().add(expectedDuration, 'seconds');
  const selectedDateTime = sessionStart
    .clone()
    .hour(selectedTime.hour())
    .minute(selectedTime.minute())
    .second(selectedTime.second())
    .millisecond(0);

  if (sessionEnd.isAfter(sessionStart, 'day') && selectedDateTime.isBefore(sessionStart)) {
    selectedDateTime.add(1, 'day');
  }

  return selectedDateTime.isBetween(sessionStart, sessionEnd, undefined, '[]')
    ? selectedDateTime
    : null;
};

export const buildTiming = (
  startTime: Moment | null,
  endTime: Moment | null,
  sessionStart: Moment | undefined,
) => {
  if (!startTime || !endTime || !sessionStart) return null;
  const startOffset = startTime.diff(sessionStart, 'seconds');
  const endOffset = endTime.diff(sessionStart, 'seconds');

  return {
    timing_type: SessionGoalTimingType.Manual,
    start_offset_seconds: startOffset,
    end_offset_seconds: endOffset,
    duration_seconds: endOffset - startOffset,
  };
};
